import { DimensionsShape } from '@play-co/timestep-core/math';
import {
  ShaderUniforms,
  Shader,
  ShaderOpts,
} from '@play-co/timestep-core/renderer';
import { Color } from '@play-co/timestep-core/ui';
import { getTextureUnits, textureAccessor } from './ShaderUtil';

const hueShift = (numTextures: number) => `
precision mediump float;

varying vec2 vTextureCoord;
varying float vAlpha;
varying float vTexIdx;
varying vec4 vColor;

uniform sampler2D uSamplers[${numTextures}];

${textureAccessor(numTextures)}

uniform float uShift;

vec3 hueShift(vec3 color, float hue) {
    vec3 k = vec3(0.57735, 0.57735, 0.57735);
    float cosAngle = cos(hue);
    return vec3(color * cosAngle + cross(k, color) * sin(hue) + k * dot(k, color) * (1.0 - cosAngle));
}

void main(void) {
  vec4 color = sampleTexture(vTextureCoord, vTexIdx) * vAlpha;
  gl_FragColor = vec4(hueShift(color.rgb, uShift), color.a);
}
`;

export type HueCycleShaderUniforms = ShaderUniforms & {
  uShift: WebGLUniformLocation;
};

export class HueShift extends Shader {
  public shift: number = 0;

  constructor(opts: ShaderOpts) {
    opts.fragmentSrc = hueShift(getTextureUnits());
    const uniforms: HueCycleShaderUniforms = {
      uShift: 0,
      uSamplers: 0,
      uResolution: 0,
    };

    super({ ...opts, uniforms });
  }

  /**
   * This method is called each time the shader is about to be used to render
   * one or more views (if batched). Here we should upload whatever uniform
   * data we may use in shaders and otherwise prepare it for rendering.
   */
  public useProgram(ctx: DimensionsShape, numTextures: number) {
    super.useProgram(ctx, numTextures);

    const gl = this._gl;
    const uniforms = this.uniforms as HueCycleShaderUniforms;

    // Upload our custom uniform data.
    gl.uniform1f(uniforms.uShift, this.shift);
  }
}

export function getHueShift(fromColor: string, toColor: string) {
  const from = new Color(fromColor);
  const to = new Color(toColor);
  const hueFrom = getHue(from);
  const hueTo = getHue(to);
  return (hueTo - hueFrom) * (Math.PI / 180);
}

export function getHue(color: Color) {
  const r = color.r / 255;
  const g = color.g / 255;
  const b = color.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  if (max === min) {
    h = 0;
  } else if (max === r) {
    h = 60 * (0 + (g - b) / (max - min));
  } else if (max === g) {
    h = 60 * (2 + (b - r) / (max - min));
  } else if (max === b) {
    h = 60 * (4 + (r - g) / (max - min));
  }
  return h;
}
